import React, {useState} from 'react';
import { Box, Typography, Modal, TextField, Button, MenuItem, Grid, } from '@mui/material';
import { textFieldStyles } from '../../Login/LoginPageStyles';
import { format, parseISO } from 'date-fns';

export const ApplyLeaveModal = ({
  openModal,
  handleCloseModal,
  leaveRequest,
  setLeaveRequest,
  handleConfirmLeave,
}) => {
  return (
    <Modal open={openModal} onClose={handleCloseModal}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: '#FFFFFF',
          padding: '24px',
          borderRadius: '8px',
          width: '400px',
        }}
      >
        <Typography variant="h6" sx={{ mb: 2, color: '#4A4A4A' }}>
          Apply for Leave
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <TextField
            label="Start Date"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={leaveRequest.startDate}
            onChange={(e) =>
              setLeaveRequest((prev) => ({ ...prev, startDate: e.target.value }))
            }
            sx={{ ...textFieldStyles, mb: 2 }}
          />
          <TextField
            label="End Date"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={leaveRequest.endDate}
            onChange={(e) =>
              setLeaveRequest((prev) => ({ ...prev, endDate: e.target.value }))
            }
            sx={{ ...textFieldStyles, mb: 2 }}
          />
          <TextField
            label="Reason for Leave"
            multiline
            rows={3}
            placeholder="Describe your reason for leave..."
            value={leaveRequest.description || ''}
            onChange={(e) =>
              setLeaveRequest((prev) => ({ ...prev, description: e.target.value }))
            }
            sx={{...textFieldStyles, mb: 2 }}
          />
          <Button
            variant="contained"
            onClick={handleConfirmLeave}
            sx={{
              backgroundColor: '#39B54A',
              '&:hover': { backgroundColor: '#2c8f39' },
            }}
          >
            Confirm Leave
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export const ViewLeaveHistoryModal = ({
  openViewModal,
  handleCloseViewModal,
  leaves,
  getStatusStyle,
}) => {
  const [statusFilter, setStatusFilter] = useState('');
  const [startDateFilter, setStartDateFilter] = useState('');
  const [endDateFilter, setEndDateFilter] = useState('');

  const handleFilterLeaves = () => {
    return leaves
      .filter((leave) => {
        if (statusFilter && leave.status !== statusFilter) {
          return false;
        }
        if (
          startDateFilter &&
          parseISO(leave.startDate) < parseISO(startDateFilter)
        ) {
          return false;
        }
        if (
          endDateFilter &&
          parseISO(leave.endDate) > parseISO(endDateFilter)
        ) {
          return false;
        }
        return true;
      })
      .sort((a, b) => parseISO(b.timestamp) - parseISO(a.timestamp)); // Sort by timestamp (most recent first)
  };

  const filteredLeaves = handleFilterLeaves();

  return (
    <Modal open={openViewModal} onClose={handleCloseViewModal}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: '#FFFFFF',
          padding: '24px',
          borderRadius: '8px',
          width: '500px',
          maxHeight: '80vh',
          overflowY: 'auto',
        }}
      >
        <Typography variant="h6" sx={{ mb: 2, color: '#4A4A4A' }}>
          Leave History
        </Typography>

        {/* Filters Section */}
        <Box
          sx={{
            p: 2,
            borderRadius: '8px',
            mb: 3,
            backgroundColor: '#F8F9FA',
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              mb: 2,
              color: '#4A4A4A',
              fontWeight: 600,
            }}
          >
            Filter Leave History
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <TextField
                select
                label="Status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                fullWidth
                variant="outlined"
                sx={{
                  ...textFieldStyles
                }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="Approved">Approved</MenuItem>
                <MenuItem value="Pending">Pending</MenuItem>
                <MenuItem value="Disapproved">Disapproved</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Start Date"
                type="date"
                value={startDateFilter}
                onChange={(e) => setStartDateFilter(e.target.value)}
                fullWidth
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                sx={{
                  ...textFieldStyles,
                  '.MuiOutlinedInput-input': {
                    fontSize: '14px',
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="End Date"
                type="date"
                value={endDateFilter}
                onChange={(e) => setEndDateFilter(e.target.value)}
                fullWidth
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                sx={{
                  ...textFieldStyles,
                  '.MuiOutlinedInput-input': {
                    fontSize: '14px',
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={12}>
              <Button
                onClick={() => {
                  setStatusFilter('');
                  setStartDateFilter('');
                  setEndDateFilter('');
                }}
                sx={{
                  color: '#39B54A',
                  borderColor: '#39B54A',
                  '&:hover': { borderColor: '#2c8f39', color: '#2c8f39' },
                }}
                variant="outlined"
                fullWidth
              >
                Reset Filters
              </Button>
            </Grid>
          </Grid>
        </Box>


        {/* Leave History */}
        {filteredLeaves.length > 0 ? (
          filteredLeaves.map((leave, index) => (
            <Box
              key={index}
              sx={{
                mb: 2,
                padding: 2,
                borderRadius: '8px',
                backgroundColor: '#F9F9F9',
              }}
            >
              <Typography variant="body2" sx={{ color: '#4A4A4A' }}>
                <strong>Leave Period:</strong>{' '}
                {`${format(parseISO(leave.startDate), 'dd MMM yyyy')} - ${format(
                  parseISO(leave.endDate),
                  'dd MMM yyyy'
                )}`}
              </Typography>
              <Typography variant="body2" sx={{ color: '#4A4A4A' }}>
                <strong>Total Hours:</strong> {leave.totalHours} hours
              </Typography>
              <Typography variant="body2" sx={{ color: '#4A4A4A' }}>
                <strong>Description:</strong> {leave.description}
              </Typography>
              <Box sx={getStatusStyle(leave.status)}>{leave.status}</Box>
            </Box>
          ))
        ) : (
          <Typography variant="body2" sx={{ color: '#4A4A4A' }}>
            No leave history available.
          </Typography>
        )}
      </Box>
    </Modal>
  );
};
